import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { usePlanning } from '../contexts/PlanningContext';
import { BattlePlan, ActionItem } from '../types/planning.types';
import { format } from 'date-fns';
import { he, enUS } from 'date-fns/locale';

export default function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, locale } = useLocale();
  const dateLocale = locale === 'he' ? he : enUS;
  const printRef = useRef<HTMLDivElement>(null);

  const {
    currentPlan,
    fetchPlan,
    updatePlan,
    deletePlan,
    addSubject,
    deleteSubject,
    addActionItem,
    deleteActionItem,
    toggleActionItem,
    addParticipant,
    removeParticipant,
  } = usePlanning();

  const [loading, setLoading] = useState(true);
  const [newSubjectTitle, setNewSubjectTitle] = useState('');
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [showAddActionItem, setShowAddActionItem] = useState<string | null>(null);
  const [newActionItem, setNewActionItem] = useState({
    description: '',
    assigneeName: '',
    priority: 'medium' as ActionItem['priority'],
    dueDate: '',
  });
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipant, setNewParticipant] = useState({ name: '', email: '', role: '' });

  useEffect(() => {
    if (id) {
      fetchPlan(id).finally(() => setLoading(false));
    }
  }, [id, fetchPlan]);

  // Calculate progress
  const calculateProgress = useCallback((plan: BattlePlan) => {
    let total = 0;
    let completed = 0;
    plan.subjects.forEach(subject => {
      subject.actionItems.forEach(item => {
        total++;
        if (item.isCompleted) completed++;
      });
    });
    return { total, completed, percent: total > 0 ? Math.round((completed / total) * 100) : 0 };
  }, []);

  // Print functionality
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent || !currentPlan) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const progress = calculateProgress(currentPlan);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="${locale === 'he' ? 'rtl' : 'ltr'}">
      <head>
        <title>${currentPlan.title}</title>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 2rem;
            max-width: 800px;
            margin: 0 auto;
            direction: ${locale === 'he' ? 'rtl' : 'ltr'};
          }
          h1 { font-size: 1.5rem; margin-bottom: 0.5rem; }
          h2 { font-size: 1.25rem; margin: 1.5rem 0 0.75rem; border-bottom: 2px solid #333; padding-bottom: 0.25rem; }
          h3 { font-size: 1rem; margin: 1rem 0 0.5rem; }
          .meta { color: #666; font-size: 0.875rem; margin-bottom: 1rem; }
          .progress-section {
            background: #f5f5f5;
            padding: 1rem;
            border-radius: 8px;
            margin-bottom: 1.5rem;
          }
          .progress-bar {
            height: 20px;
            background: #ddd;
            border-radius: 10px;
            overflow: hidden;
            margin-top: 0.5rem;
          }
          .progress-fill {
            height: 100%;
            background: #22c55e;
            border-radius: 10px;
          }
          .participants { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1rem; }
          .participant {
            background: #e0e7ff;
            padding: 0.25rem 0.75rem;
            border-radius: 1rem;
            font-size: 0.875rem;
          }
          .subject { margin-bottom: 1.5rem; }
          .action-item {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;
            padding: 0.5rem 0;
            border-bottom: 1px solid #eee;
          }
          .checkbox {
            width: 18px;
            height: 18px;
            border: 2px solid #333;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .checkbox.checked { background: #22c55e; border-color: #22c55e; color: white; }
          .action-content { flex: 1; }
          .action-meta { font-size: 0.75rem; color: #666; margin-top: 0.25rem; }
          .completed { text-decoration: line-through; color: #888; }
          .notes { margin-top: 1.5rem; padding: 1rem; background: #f9f9f9; border-radius: 8px; }
          .notes-content { white-space: pre-wrap; }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>${currentPlan.title}</h1>
        ${currentPlan.description ? `<p class="meta">${currentPlan.description}</p>` : ''}
        <p class="meta">
          📅 ${format(new Date(currentPlan.date), 'dd MMMM yyyy', { locale: dateLocale })} •
          ${t('planning.createdBy')} ${currentPlan.createdByName}
        </p>

        <div class="progress-section">
          <strong>${t('planning.progress')}: ${progress.completed}/${progress.total} (${progress.percent}%)</strong>
          <div class="progress-bar">
            <div class="progress-fill" style="width: ${progress.percent}%"></div>
          </div>
        </div>

        ${currentPlan.participants.length > 0 ? `
          <h2>${t('planning.participants')}</h2>
          <div class="participants">
            ${currentPlan.participants.map(p => `<span class="participant">${p.name}${p.role ? ` (${p.role})` : ''}</span>`).join('')}
          </div>
        ` : ''}

        ${currentPlan.subjects.length > 0 ? `
          <h2>${t('planning.subjectsAndTasks')}</h2>
          ${currentPlan.subjects.map(subject => `
            <div class="subject">
              <h3>${subject.title}</h3>
              ${subject.actionItems.length > 0 ? subject.actionItems.map(item => `
                <div class="action-item">
                  <div class="checkbox ${item.isCompleted ? 'checked' : ''}">${item.isCompleted ? '✓' : ''}</div>
                  <div class="action-content">
                    <div class="${item.isCompleted ? 'completed' : ''}">${item.description}</div>
                    <div class="action-meta">
                      ${item.assigneeName ? `👤 ${item.assigneeName}` : ''}
                      ${item.dueDate ? ` • 📅 ${format(new Date(item.dueDate), 'dd/MM/yyyy')}` : ''}
                    </div>
                  </div>
                </div>
              `).join('') : `<p style="color: #888; font-size: 0.875rem;">${t('planning.noTasksInSubject')}</p>`}
            </div>
          `).join('')}
        ` : ''}

        ${currentPlan.notes ? `
          <div class="notes">
            <h2>${t('planning.notes')}</h2>
            <div class="notes-content">${currentPlan.notes}</div>
          </div>
        ` : ''}
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('planning.loadingPlan')}</p>
      </div>
    );
  }

  if (!currentPlan) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">❌</div>
        <h3 className="empty-state-title">{t('planning.planNotFound')}</h3>
        <button className="btn btn-primary" onClick={() => navigate('/plans')}>
          {t('planning.backToPlans')}
        </button>
      </div>
    );
  }

  const handleAddSubject = async () => {
    if (!newSubjectTitle.trim() || !id) return;
    await addSubject(id, {
      title: newSubjectTitle,
      order: currentPlan.subjects.length,
    });
    setNewSubjectTitle('');
    setShowAddSubject(false);
  };

  const handleAddActionItem = async (subjectId: string) => {
    if (!newActionItem.description.trim() || !id) return;
    await addActionItem(id, subjectId, {
      ...newActionItem,
      assigneeId: '',
      isCompleted: false,
    });
    setNewActionItem({ description: '', assigneeName: '', priority: 'medium', dueDate: '' });
    setShowAddActionItem(null);
  };

  const handleAddParticipant = async () => {
    if (!newParticipant.name.trim() || !id) return;
    await addParticipant(id, newParticipant);
    setNewParticipant({ name: '', email: '', role: '' });
    setShowAddParticipant(false);
  };

  const handleStatusChange = async (status: BattlePlan['status']) => {
    if (!id) return;
    await updatePlan(id, { status });
  };

  const handleDeletePlan = async () => {
    if (!id) return;
    if (window.confirm(t('planning.confirmDelete'))) {
      await deletePlan(id);
      navigate('/plans');
    }
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const progress = calculateProgress(currentPlan);

  return (
    <div className="plan-detail" ref={printRef}>
      {/* Back Button */}
      <button
        className="btn btn-secondary no-print"
        style={{ marginBottom: 'var(--spacing-md)' }}
        onClick={() => navigate('/plans')}
      >
        {locale === 'he' ? '→' : '←'} {t('planning.backToPlans')}
      </button>

      {/* Header */}
      <div className="plan-detail-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 className="plan-detail-title">{currentPlan.title}</h1>
            {currentPlan.description && (
              <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--spacing-md)' }}>
                {currentPlan.description}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }} className="no-print">
            <button className="btn btn-secondary" onClick={handlePrint} title={t('planning.printPlan')}>
              🖨️ {t('common.print')}
            </button>
            <select
              className="form-select"
              style={{ width: 'auto' }}
              value={currentPlan.status}
              onChange={(e) => handleStatusChange(e.target.value as BattlePlan['status'])}
            >
              <option value="draft">{t('planning.statusDraft')}</option>
              <option value="active">{t('planning.statusActive')}</option>
              <option value="completed">{t('planning.statusCompleted')}</option>
              <option value="archived">{t('planning.statusArchived')}</option>
            </select>
            <button className="btn btn-danger" onClick={handleDeletePlan}>
              {t('planning.deletePlan')}
            </button>
          </div>
        </div>

        <div className="plan-detail-meta">
          <span>📅 {format(new Date(currentPlan.date), 'dd MMMM yyyy', { locale: dateLocale })}</span>
          <span>✍️ {t('planning.createdBy')} {currentPlan.createdByName}</span>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="progress-card">
        <div className="progress-card-header">
          <h3>{t('planning.progress')}</h3>
          <span className="progress-big-percent">{progress.percent}%</span>
        </div>
        <div className="progress-bar-large">
          <div
            className="progress-fill-large"
            style={{
              width: `${progress.percent}%`,
              backgroundColor: progress.percent === 100 ? 'var(--success-color)' : 'var(--primary-color)'
            }}
          ></div>
        </div>
        <div className="progress-stats">
          <span>{t('planning.progressLabel').replace('{completed}', String(progress.completed)).replace('{total}', String(progress.total))}</span>
        </div>
      </div>

      {/* Participants Section */}
      <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="card-header">
          <h3 className="card-title">{t('planning.participants')} ({currentPlan.participants.length})</h3>
          <button className="btn btn-sm btn-primary no-print" onClick={() => setShowAddParticipant(true)}>
            + {t('planning.addParticipant')}
          </button>
        </div>
        <div className="card-body">
          {currentPlan.participants.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('planning.noParticipants')}</p>
          ) : (
            <div className="participants-list">
              {currentPlan.participants.map(participant => (
                <div key={participant.id} className="participant-chip">
                  <div className="participant-avatar">
                    {participant.name[0]}
                  </div>
                  <span>{participant.name}</span>
                  {participant.role && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      ({participant.role})
                    </span>
                  )}
                  <button
                    className="btn btn-icon btn-sm no-print"
                    style={{ marginInlineStart: 'auto', padding: '2px' }}
                    onClick={() => id && removeParticipant(id, participant.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          {showAddParticipant && (
            <div className="no-print" style={{ marginTop: 'var(--spacing-md)', padding: 'var(--spacing-md)', background: 'var(--background-color)', borderRadius: 'var(--radius-md)' }}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder={t('planning.participantName')}
                  className="form-input"
                  value={newParticipant.name}
                  onChange={(e) => setNewParticipant({ ...newParticipant, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  placeholder={t('planning.participantEmail')}
                  className="form-input"
                  value={newParticipant.email}
                  onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <input
                  type="text"
                  placeholder={t('planning.participantRole')}
                  className="form-input"
                  value={newParticipant.role}
                  onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <button className="btn btn-primary btn-sm" onClick={handleAddParticipant}>
                  {t('common.add')}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowAddParticipant(false)}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subjects and Action Items */}
      <div className="card" style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div className="card-header">
          <h3 className="card-title">{t('planning.subjectsAndTasks')}</h3>
          <button className="btn btn-sm btn-primary no-print" onClick={() => setShowAddSubject(true)}>
            + {t('planning.addSubject')}
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {showAddSubject && (
            <div className="no-print" style={{ padding: 'var(--spacing-md)', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                <input
                  type="text"
                  placeholder={t('planning.subjectTitle')}
                  className="form-input"
                  value={newSubjectTitle}
                  onChange={(e) => setNewSubjectTitle(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddSubject()}
                />
                <button className="btn btn-primary" onClick={handleAddSubject}>{t('common.add')}</button>
                <button className="btn btn-secondary" onClick={() => setShowAddSubject(false)}>{t('common.cancel')}</button>
              </div>
            </div>
          )}

          {currentPlan.subjects.length === 0 ? (
            <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--text-secondary)' }}>
              <p>{t('planning.noSubjects')}</p>
              <p style={{ fontSize: '0.875rem' }}>{t('planning.noSubjectsDescription')}</p>
            </div>
          ) : (
            <div className="subjects-list" style={{ padding: 0 }}>
              {currentPlan.subjects.map(subject => (
                <div key={subject.id} className="subject-card" style={{ boxShadow: 'none', borderRadius: 0, borderBottom: '1px solid var(--border-color)' }}>
                  <div className="subject-header">
                    <h4 className="subject-title">{subject.title}</h4>
                    <div className="subject-actions no-print">
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setShowAddActionItem(subject.id)}
                      >
                        + {t('planning.addTask')}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => id && deleteSubject(id, subject.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  {showAddActionItem === subject.id && (
                    <div className="no-print" style={{ padding: 'var(--spacing-md)', background: 'var(--surface-color)', borderBottom: '1px solid var(--border-color)' }}>
                      <div className="form-group">
                        <input
                          type="text"
                          placeholder={t('planning.taskDescription')}
                          className="form-input"
                          value={newActionItem.description}
                          onChange={(e) => setNewActionItem({ ...newActionItem, description: e.target.value })}
                        />
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--spacing-sm)' }}>
                        <input
                          type="text"
                          placeholder={t('planning.assignee')}
                          className="form-input"
                          value={newActionItem.assigneeName}
                          onChange={(e) => setNewActionItem({ ...newActionItem, assigneeName: e.target.value })}
                        />
                        <select
                          className="form-select"
                          value={newActionItem.priority}
                          onChange={(e) => setNewActionItem({ ...newActionItem, priority: e.target.value as ActionItem['priority'] })}
                        >
                          <option value="low">{t('planning.priorityLow')}</option>
                          <option value="medium">{t('planning.priorityMedium')}</option>
                          <option value="high">{t('planning.priorityHigh')}</option>
                        </select>
                        <input
                          type="date"
                          className="form-input"
                          value={newActionItem.dueDate}
                          onChange={(e) => setNewActionItem({ ...newActionItem, dueDate: e.target.value })}
                        />
                      </div>
                      <div style={{ marginTop: 'var(--spacing-sm)', display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button className="btn btn-primary btn-sm" onClick={() => handleAddActionItem(subject.id)}>
                          {t('common.add')}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setShowAddActionItem(null)}>
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {subject.actionItems.length === 0 ? (
                    <div style={{ padding: 'var(--spacing-md)', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      {t('planning.noTasksInSubject')}
                    </div>
                  ) : (
                    <div className="action-items-list">
                      {subject.actionItems.map(item => (
                        <div key={item.id} className={`action-item ${item.isCompleted ? 'completed' : ''}`}>
                          <div
                            className={`action-checkbox ${item.isCompleted ? 'checked' : ''}`}
                            onClick={() => id && toggleActionItem(id, subject.id, item.id)}
                          >
                            {item.isCompleted && '✓'}
                          </div>
                          <div className="action-content">
                            <div className="action-description">{item.description}</div>
                            <div className="action-meta">
                              {item.assigneeName && (
                                <span className="action-assignee">👤 {item.assigneeName}</span>
                              )}
                              {item.dueDate && (
                                <span className={`action-due-date ${isOverdue(item.dueDate) && !item.isCompleted ? 'overdue' : ''}`}>
                                  📅 {format(new Date(item.dueDate), 'dd/MM/yyyy')}
                                </span>
                              )}
                              <span className={`priority-badge ${item.priority}`}>
                                {item.priority === 'high' && t('planning.high')}
                                {item.priority === 'medium' && t('planning.medium')}
                                {item.priority === 'low' && t('planning.low')}
                              </span>
                            </div>
                          </div>
                          <button
                            className="btn btn-icon btn-sm no-print"
                            style={{ opacity: 0.5 }}
                            onClick={() => id && deleteActionItem(id, subject.id, item.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{t('planning.notes')}</h3>
        </div>
        <div className="card-body">
          <textarea
            className="form-textarea"
            placeholder={t('planning.notesPlaceholder')}
            value={currentPlan.notes || ''}
            onChange={(e) => id && updatePlan(id, { notes: e.target.value })}
            style={{ minHeight: '150px' }}
          />
        </div>
      </div>

      <style>{`
        .progress-card {
          background: var(--surface-color);
          border-radius: var(--radius-lg);
          padding: var(--spacing-lg);
          margin-bottom: var(--spacing-lg);
          box-shadow: var(--shadow-md);
        }
        .progress-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-md);
        }
        .progress-card-header h3 {
          margin: 0;
          font-size: 1rem;
          color: var(--text-secondary);
        }
        .progress-big-percent {
          font-size: 2rem;
          font-weight: 700;
          color: var(--primary-color);
        }
        .progress-bar-large {
          height: 16px;
          background: var(--border-color);
          border-radius: 8px;
          overflow: hidden;
        }
        .progress-fill-large {
          height: 100%;
          border-radius: 8px;
          transition: width 0.5s ease, background-color 0.3s ease;
        }
        .progress-stats {
          margin-top: var(--spacing-sm);
          font-size: 0.875rem;
          color: var(--text-secondary);
        }
        @media print {
          .no-print { display: none !important; }
          .progress-card { break-inside: avoid; }
          .card { break-inside: avoid; box-shadow: none; border: 1px solid #ddd; }
        }
      `}</style>
    </div>
  );
}
